// Elintra — Landing Page | v3.2 | UAG v3.0.0
// Base: Ratified v3.1 LandingPage (ProtocolGraph, LiveTransactionTicker,
// AnimatedCounter, AEO Score section, Settlement Rails section, Final CTA).
// v3.2 delta: x402 hero badge, x402 LIVE counter signal, Algorand x402
// settlement rail card, updated protocol count (5 surfaces), trust atoms,
// dual-attestation governance copy, elintra.algo footer.

import { useState, useEffect } from 'react';
import {
  motion, AnimatePresence,
  useTransform, useSpring,
} from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Globe, Plug, TrendingUp,
  Zap, Shield,
} from 'lucide-react';
import { Button }          from '@/components/ui/Button';
import { Badge }           from '@/components/ui/Badge';
import { Card }            from '@/components/ui/Card';
import { AnimatedSection } from '@/components/ui/AnimatedSection';
import {
  generateMockQuery,
} from '@/lib/fixtures';
import { formatMoneyString } from '@/lib/utils';
import type { Route, AgentQuery, Protocol } from '@/types/app';

// ─── Protocol label colors ────────────────────────────────────────────────
const PROTOCOL_LABEL_COLOR: Record<Protocol, string> = {
  A2A:  '#8B5CF6',
  ACP:  '#3B82F6',
  UCP:  '#10B981',
  NLIP: '#F59E0B',
};

// ─── Live Transaction Ticker ──────────────────────────────────────────────
// The single most important proof-of-life component. Fires before the user
// reads a word. Not decoration — it is the product demonstrating itself.

function LiveTransactionTicker({ queries }: { queries: AgentQuery[] }) {
  if (queries.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 w-full max-w-lg">
      <AnimatePresence initial={false} mode="popLayout">
        {queries.slice(0, 4).map((q) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, x: 24, scale: 0.98 }}
            animate={{ opacity: 1, x: 0,  scale: 1    }}
            exit={{    opacity: 0, x: -16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg
                       bg-zinc-900/80 border border-zinc-800/60 backdrop-blur-sm
                       text-xs font-mono"
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{
                backgroundColor: PROTOCOL_LABEL_COLOR[q.protocol],
                boxShadow:       `0 0 6px ${PROTOCOL_LABEL_COLOR[q.protocol]}`,
              }} />
            <span className="text-zinc-500 shrink-0 w-[28px]">{q.protocol}</span>
            <span className="text-zinc-400 truncate flex-1">{q.queryIntent}</span>
            {q.outcome === 'PURCHASED' && q.amount != null ? (
              <span className="text-emerald-400 shrink-0 font-semibold">
                {formatMoneyString(q.amount)}
              </span>
            ) : q.outcome === 'MATCHED' ? (
              <span className="text-blue-400 shrink-0">matched</span>
            ) : (
              <span className="text-zinc-700 shrink-0">rejected</span>
            )}
            <span className="text-zinc-700 shrink-0">{q.latencyMs != null ? `${q.latencyMs}ms` : ''}</span>
            {/* v3.2: show ALGO badge on x402 purchases */}
            {q.settlementRail === 'algorand_x402' && (
              <span className="text-emerald-400 shrink-0 font-semibold text-[9px] border
                               border-emerald-500/30 rounded px-1 py-0.5">ALGO</span>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Protocol Node Graph ──────────────────────────────────────────────────
// Live pulse radiates from Elintra outward to each agent node on each
// transaction. Authority diagram — not decoration.

function ProtocolGraph({ pulseProtocol }: { pulseProtocol: Protocol | null }) {
  const NODE_AGENTS = [
    { label: 'Claude',     y: 60,  color: '#8B5CF6' },
    { label: 'Gemini',     y: 160, color: '#10B981' },
    { label: 'Perplexity', y: 260, color: '#F59E0B' },
    { label: 'GPT',        y: 360, color: '#3B82F6' },
  ];

  return (
    <div className="relative w-full h-[380px]">
      <svg className="w-full h-full" viewBox="0 0 560 420" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="glow-hard">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glow-soft">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#8B5CF6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0"    />
          </radialGradient>
          {NODE_AGENTS.map((a) => (
            <linearGradient key={a.label} id={`wire-${a.label}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor={a.color} stopOpacity="0.1" />
              <stop offset="50%"  stopColor={a.color} stopOpacity="0.4" />
              <stop offset="100%" stopColor={a.color} stopOpacity="0.1" />
            </linearGradient>
          ))}
        </defs>

        {/* Hub ambient glow */}
        <circle cx="280" cy="210" r="80" fill="url(#hub-glow)" />

        {/* Wires — one per agent */}
        {NODE_AGENTS.map((a) => (
          <line key={a.label}
            x1="90" y1={a.y + 20} x2="238" y2="210"
            stroke={`url(#wire-${a.label})`} strokeWidth="1.5"
          />
        ))}
        {/* Output wire to merchant */}
        <line x1="322" y1="210" x2="480" y2="210"
          stroke="#27272A" strokeWidth="1.5" strokeDasharray="6 5" />

        {/* Pulse particles — one fires per transaction */}
        {NODE_AGENTS.map((a) => (
          <motion.circle key={`pulse-${a.label}`}
            r={4} fill={a.color} filter="url(#glow-hard)"
            initial={{ cx: 90, cy: a.y + 20, opacity: 0 }}
            animate={pulseProtocol ? {
              cx:      [90,  238, 280],
              cy:      [a.y + 20, 210, 210],
              opacity: [0, 1, 0],
            } : { opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          />
        ))}
        <motion.circle r={5} fill="#FAFAFA" filter="url(#glow-hard)"
          initial={{ cx: 280, cy: 210, opacity: 0 }}
          animate={pulseProtocol ? {
            cx:      [280, 480],
            cy:      [210, 210],
            opacity: [0, 1, 0],
          } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.55, ease: 'easeIn' }}
        />

        {/* Agent nodes */}
        {NODE_AGENTS.map((a) => (
          <g key={a.label} transform={`translate(90, ${a.y + 20})`}>
            <circle r="30" fill="#18181B" stroke={a.color} strokeWidth="1.2" opacity={0.7} />
            <text y="5" fill="#A1A1AA" fontSize="10" textAnchor="middle" fontFamily="system-ui">
              {a.label}
            </text>
          </g>
        ))}

        {/* Elintra hub */}
        <g transform="translate(280, 210)">
          <motion.circle r="55"
            fill="none" stroke="#8B5CF6" strokeWidth="1"
            animate={{ r: [50, 58, 50], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
          <circle r="42" fill="#18181B" stroke="#8B5CF6" strokeWidth="2" />
          <text y="-6" fill="#FAFAFA" fontSize="12" fontWeight="bold"
            textAnchor="middle" fontFamily="system-ui">
            Elintra
          </text>
          <text y="11" fill="#8B5CF6" fontSize="9" textAnchor="middle" fontFamily="monospace">
            LIVE
          </text>
        </g>

        {/* Merchant endpoint */}
        <g transform="translate(480, 210)">
          <rect x="-38" y="-38" width="76" height="76" rx="12"
            fill="#18181B" stroke="#27272A" strokeWidth="1.5" />
          <text y="-6"  fill="#FAFAFA" fontSize="10" textAnchor="middle" fontFamily="system-ui">Your</text>
          <text y="10"  fill="#FAFAFA" fontSize="10" textAnchor="middle" fontFamily="system-ui">Store</text>
        </g>

        {/* Protocol badges along wires */}
        {(['A2A', 'ACP', 'UCP', 'NLIP'] as Protocol[]).map((p, i) => (
          <g key={p} transform={`translate(175, ${65 + i * 78})`}>
            <rect x="-18" y="-9" width="36" height="18" rx="4"
              fill="#09090b" stroke={PROTOCOL_LABEL_COLOR[p]} strokeWidth="0.8" opacity="0.8" />
            <text y="4" fill={PROTOCOL_LABEL_COLOR[p]} fontSize="8.5"
              fontFamily="monospace" fontWeight="600" textAnchor="middle">
              {p}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Animated Counter (framer-motion spring) ──────────────────────────────
function AnimatedCounter({ value }: { value: number }) {
  const spring  = useSpring(value, { stiffness: 80, damping: 25 });
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString());
  useEffect(() => { spring.set(value); }, [value, spring]);
  return <motion.span>{display}</motion.span>;
}

// ─── Pricing data ─────────────────────────────────────────────────────────
const PRICING = [
  {
    name: 'Explorer', tagline: 'See if agents can find you.',
    price: { monthly: 0, annual: 0 }, highlighted: false, cta: 'Start free — no card',
    features: [
      '1 store connected',
      '100 agent queries / month',
      'Basic AEO Analytics',
      'Community support',
    ],
  },
  {
    name: 'Creator', tagline: 'Earn while you sleep.',
    price: { monthly: 29, annual: 24 }, highlighted: true, cta: 'Start 14-day trial',
    features: [
      '3 stores connected',
      'Unlimited agent queries',
      'Full agent behavior data',
      'Intent Graph access',
      'Priority routing network',
      'Email support',
    ],
  },
  {
    name: 'Studio', tagline: 'Enterprise-grade agent commerce.',
    price: { monthly: 99, annual: 79 }, highlighted: false, cta: 'Contact Sales',
    features: [
      'Unlimited stores',
      'Intent Graph API',
      'White-label reports',
      'Dedicated AEO strategist',
      'SLA guarantee',
    ],
  },
] as const;

// ─── Main Component ───────────────────────────────────────────────────────
export const LandingPage = ({
  totalQueries, onNavigate,
}: {
  totalQueries: number;
  onNavigate:   (r: Route) => void;
}) => {
  const [isAnnual,      setIsAnnual]      = useState(false);
  const [liveQueries,   setLiveQueries]   = useState<AgentQuery[]>([]);
  const [pulseProtocol, setPulseProtocol] = useState<Protocol | null>(null);
  const [liveCount,     setLiveCount]     = useState(totalQueries);

  // Heartbeat — fires every 2.8s.
  // Adds a query to the ticker, pulses the graph, increments the counter.
  // Must run before the user reads anything — immediate first fire.
  useEffect(() => {
    const fire = () => {
      const q = generateMockQuery();
      setLiveQueries((prev) => [q, ...prev].slice(0, 4));
      setPulseProtocol(q.protocol);
      setLiveCount((n) => n + 1);
      setTimeout(() => setPulseProtocol(null), 1_200);
    };
    fire();
    const id = setInterval(fire, 2_800);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 overflow-x-hidden">

      {/* ── Navigation ──────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50
                      bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 text-lg font-bold tracking-tight">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center
                            shadow-[0_0_20px_rgba(139,92,246,0.6)]">
              <Zap className="w-4 h-4 text-white" />
            </div>
            Elintra
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-500">
            {[
              ['How it works', 'how'],
              ['Pricing',      'pricing'],
              ['Docs',         ''],
            ].map(([label, id]) => (
              <a key={label} href={id ? `#${id}` : '#'}
                onClick={(e) => {
                  e.preventDefault();
                  if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="hover:text-zinc-50 transition-colors cursor-pointer">
                {label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden md:flex"
              onClick={() => onNavigate('app')}>
              Sign in
            </Button>
            <Button onClick={() => onNavigate('onboarding')}
              className="shadow-[0_0_20px_rgba(139,92,246,0.4)]">
              Get early access
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-16 px-6 min-h-screen flex items-center overflow-hidden">

        {/* Ambient glow fields — atmosphere before content */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2
                        w-[900px] h-[600px] bg-violet-600/8 blur-[160px] rounded-full
                        pointer-events-none" />
        <div className="absolute top-1/3 left-[10%]
                        w-[400px] h-[400px] bg-blue-600/6 blur-[120px] rounded-full
                        pointer-events-none" />
        <div className="absolute bottom-0 right-[10%]
                        w-[350px] h-[350px] bg-violet-800/5 blur-[100px] rounded-full
                        pointer-events-none" />

        {/* Subtle grid overlay */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
          }}
        />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">

          {/* Left: The Declaration */}
          <div>
            {/* Status signal + v3.2 x402 badge — fires before headline */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex items-center gap-3 mb-8 flex-wrap"
            >
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full
                              border border-violet-500/30 bg-violet-500/8 text-xs font-medium text-violet-300">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                Gateway operational · {liveCount.toLocaleString()} transactions routed
              </div>
              {/* v3.2: x402 LIVE indicator in hero */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                              border border-emerald-500/25 bg-emerald-500/8
                              text-xs font-medium text-emerald-400 font-mono">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                x402 LIVE · elintra.algo
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
              className="text-5xl md:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.07] mb-6"
            >
              The assumption<br />
              <span className="text-zinc-500">that a human presses</span><br />
              <span className="text-transparent bg-clip-text
                               bg-gradient-to-r from-violet-400 via-violet-300 to-violet-500">
                checkout is&nbsp;false.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="text-lg text-zinc-400 mb-4 leading-relaxed max-w-lg"
            >
              In 2026, autonomous AI agents execute the entire discovery-to-checkout
              workflow on behalf of users. The 50&nbsp;million independent digital
              creators are completely invisible to them.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              className="text-base text-zinc-500 mb-10 leading-relaxed max-w-lg"
            >
              Elintra translates your storefront into machine-readable commerce —
              discoverable, evaluable, and purchasable by every agent on every rail.
              The transactions you're watching happen right now.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.55 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Button size="lg" className="h-14 px-8 text-base
                               shadow-[0_0_30px_rgba(139,92,246,0.4)]
                               hover:shadow-[0_0_40px_rgba(139,92,246,0.6)]
                               transition-shadow duration-300"
                onClick={() => onNavigate('onboarding')}>
                Connect your store <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base"
                onClick={() => onNavigate('app')}>
                See live dashboard
              </Button>
            </motion.div>

            {/* Trust atoms — v3.2: Algorand x402 added */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.7 }}
              className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-600"
            >
              {[
                'A2A · ACP · UCP · NLIP · x402',
                'Stripe · Algorand x402 · Bitcoin Lightning · XRPL',
                'Constitutional governance on every transaction',
              ].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-zinc-700" />
                  {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right: The Live System — proof before explanation */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            className="flex flex-col gap-6"
          >
            {/* Graph */}
            <div className="relative rounded-2xl border border-zinc-800/60
                            bg-zinc-900/30 backdrop-blur-sm p-4 overflow-hidden">
              <div className="absolute top-0 right-0 bottom-0 left-0
                              bg-gradient-to-b from-violet-500/5 to-transparent pointer-events-none" />
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs font-mono text-zinc-600">AGENT → ELINTRA → MERCHANT</span>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  LIVE
                </div>
              </div>
              <ProtocolGraph pulseProtocol={pulseProtocol} />
            </div>

            {/* Ticker */}
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-mono text-zinc-600 uppercase tracking-wider">
                  Live Agent Feed
                </span>
                <span className="text-xs font-mono text-zinc-700">real-time</span>
              </div>
              <LiveTransactionTicker queries={liveQueries} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Intent Graph Counter — momentum ─────────────────────────────── */}
      <section className="border-y border-zinc-800/60 bg-zinc-900/20 py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            {
              value: <AnimatedCounter value={liveCount} />,
              suffix: '+',
              label: 'Agent Transactions Routed',
              sub: 'Intent Graph events logged',
            },
            {
              value: '142,589',
              suffix: '',
              label: 'Intent Graph Size',
              sub: 'Unique agent intent vectors',
            },
            {
              // v3.2: 5 surfaces (A2A, ACP, UCP, NLIP + x402 PAYMENT_LAYER)
              value: '5',
              suffix: ' surfaces',
              label: 'Protocol Surfaces Active',
              sub: 'A2A · ACP · UCP · NLIP · x402',
            },
          ].map(({ value, suffix, label, sub }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className="text-4xl md:text-5xl font-bold font-mono tracking-tight
                              text-transparent bg-clip-text bg-gradient-to-b from-zinc-50 to-zinc-400">
                {value}{suffix}
              </div>
              <p className="text-sm font-semibold text-zinc-300">{label}</p>
              <p className="text-xs text-zinc-600">{sub}</p>
            </div>
          ))}
        </div>
        {/* v3.2: x402 LIVE signal strip below counter */}
        <div className="max-w-7xl mx-auto px-6 flex justify-center mt-6 gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full
                           bg-emerald-500/8 border border-emerald-500/20
                           text-emerald-400 text-xs font-semibold font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            x402 LIVE · elintra.algo · GoPlausible · 4s FINAL
          </span>
          <span className="text-xs text-zinc-700 self-center">Algorand mainnet · Phase 2A active</span>
        </div>
      </section>

      {/* ── How It Works — system, not feature list ──────────────────────── */}
      <section id="how" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-20">
            <Badge variant="primary" className="mb-4">The Architecture</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              Not a plugin. A constitutional gateway.
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              Most platforms bolt on AI compatibility as a feature. Elintra is built
              from the ground up as the protocol translation layer between your
              existing commerce and the autonomous agent economy.
            </p>
          </AnimatedSection>

          {/* Six capabilities */}
          <div className="grid md:grid-cols-3 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[14%] right-[14%] h-px
                            bg-gradient-to-r from-transparent via-violet-500/30 to-transparent" />
            {[
              {
                n: '01', icon: Plug, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20',
                title: 'Translation',
                desc: 'Every product becomes ACP-compliant JSON, A2A capability manifests, UCP catalog entries, and NLIP intent vectors. Simultaneously.',
              },
              {
                n: '02', icon: Globe, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20',
                title: 'Exposition',
                desc: 'Protocol endpoints provisioned at /.well-known/a2a, /acp, /ucp, /nlip, /x402. These are the addresses agents use to discover you.',
              },
              {
                n: '03', icon: Zap, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20',
                title: 'Settlement Routing',
                desc: 'Stripe for fiat agents. Algorand x402 for Constitutional Commerce agents. Bitcoin Lightning for crypto-native agents. XRPL for institutional.',
              },
              {
                n: '04', icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20',
                title: 'Intelligence',
                desc: 'Every interaction becomes Intent Graph data. Match rates, rejection taxonomy, price elasticity by agent class. Your AEO score compounds monthly.',
              },
              {
                n: '05', icon: Shield, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20',
                // v3.2: governance copy updated for dual attestation
                title: 'Constitutional Governance',
                desc: 'A governance_hash and Algorand TX ID are computed on every x402 transaction — dual attestation proving the compliance evaluation occurred on-chain.',
              },
              {
                n: '→', icon: ArrowRight, color: 'text-zinc-500', bg: 'bg-zinc-800 border-zinc-700',
                title: 'The window is open now.',
                desc: 'The infrastructure buildout phase of agentic commerce is Q2 2026. The Intent Graph only accumulates by being the gateway. Every week of delay cannot be recovered.',
              },
            ].map(({ n, icon: Icon, color, bg, title, desc }, i) => (
              <AnimatedSection key={n} delay={i * 0.07}
                className="relative z-10 p-6 rounded-2xl border border-zinc-800/60
                           bg-zinc-900/30 hover:bg-zinc-900/60 hover:border-zinc-700
                           transition-all duration-300 group">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-5 border ${bg}`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xs font-mono text-zinc-700">{n}</span>
                  <h3 className="font-bold text-base">{title}</h3>
                </div>
                <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── AEO Score — the data moat made tangible ──────────────────────── */}
      <section className="py-24 px-6 border-y border-zinc-800/40 bg-zinc-900/10">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <Badge variant="acp" className="mb-4">The Data Moat</Badge>
            <h2 className="text-3xl font-bold mb-4 leading-tight tracking-tight">
              Your AEO Score:<br />
              <span className="text-zinc-400">data equity that compounds.</span>
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-6">
              Most analytics tell you what happened. The AEO Score tells you
              how AI agents evaluate you — by protocol, by agent class,
              by price sensitivity, by rejection pattern.
            </p>
            <p className="text-zinc-500 text-sm leading-relaxed">
              It is computed from Elintra's proprietary Intent Graph. It is
              non-portable. A creator who leaves Elintra loses their accumulated
              behavioral intelligence. No competitor can replicate six months of
              agent interaction history. The AEO Score is not a retention
              mechanism — it is compounding data equity.
            </p>
          </AnimatedSection>

          {/* AEO Score Visual Demo */}
          <AnimatedSection delay={0.1}>
            <Card className="p-8 border-violet-500/20 shadow-[0_0_40px_rgba(139,92,246,0.08)]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Store AEO Score</p>
                  <p className="text-xs text-zinc-700 font-mono">Creator OS Notion System</p>
                </div>
                <Badge variant="success">↑ Strong</Badge>
              </div>
              <div className="relative w-36 h-36 mx-auto mb-6">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none"
                    stroke="#27272A" strokeWidth="8" />
                  <motion.circle cx="60" cy="60" r="50" fill="none"
                    stroke="#8B5CF6" strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 50 }}
                    whileInView={{ strokeDashoffset: 2 * Math.PI * 50 * (1 - 0.87) }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold font-mono">87</span>
                  <span className="text-xs text-zinc-500 mt-0.5">/ 100</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center border-t border-zinc-800 pt-5">
                {[
                  { label: 'A2A Match',   value: '94%',   color: 'text-violet-400'  },
                  { label: 'ACP Conv.',   value: '31%',   color: 'text-blue-400'    },
                  { label: 'Avg Latency', value: '142ms', color: 'text-emerald-400' },
                ].map(({ label, value, color }) => (
                  <div key={label}>
                    <p className={`text-base font-bold font-mono ${color}`}>{value}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Settlement Rails — v3.2: 4 rails (Algorand x402 Phase 2 active) ── */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <Badge variant="default" className="mb-4">Settlement Architecture</Badge>
            <h2 className="text-3xl font-bold mb-3 tracking-tight">
              Every agent. Every payment type. One gateway.
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto text-sm leading-relaxed">
              The only creator commerce platform wired for fiat AI agents,
              crypto-native agents, and institutional agents simultaneously —
              now with Constitutional Commerce on Algorand.
            </p>
          </AnimatedSection>

          {/* v3.2: 4 cards — algorand_x402 added as Phase 2 active lead */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                phase: 'Phase 1', status: 'Live',
                variant: 'success' as const,
                rail: 'Stripe ACP',
                desc: 'Consumer AI agents paying in fiat. Stripe processes; Elintra routes.',
                technical: 'HTTP 402 · ACP Checkout · SharedPaymentToken',
                highlight: false,
              },
              {
                // v3.2: Algorand x402 — Phase 2A active
                phase: 'Phase 2A', status: 'Live',
                variant: 'success' as const,
                rail: 'Algorand x402 (USDT-ASA)',
                desc: 'Constitutional Commerce agents settle USDT on Algorand mainnet. 4-second finality. GoPlausible facilitation. Dual attestation on every transaction.',
                technical: 'elintra.algo · algorand-mainnet · GoPlausible · 4s FINAL',
                highlight: true,
              },
              {
                phase: 'Phase 2B', status: 'Ready',
                variant: 'info' as const,
                rail: 'Bitcoin + USDT Lightning',
                desc: 'Crypto-native agents. Near-instant settlement at near-zero cost.',
                technical: 'x402 · elintra256@tether.me · Taproot Assets',
                highlight: false,
              },
              {
                phase: 'Phase 3', status: 'Preview',
                variant: 'warning' as const,
                rail: 'XRPL RLUSD (XLS-80)',
                desc: 'Institutional AI agents with compliance credentials. Unlocks corporate AI buyer class.',
                technical: 'XLS-80 Permissioned Domains · XLS-70 Credentials',
                highlight: false,
              },
            ].map(({ phase, status, variant, rail, desc, technical, highlight }) => (
              <AnimatedSection key={phase}
                className={`p-6 rounded-2xl border transition-all duration-300
                            ${highlight
                              ? 'border-emerald-500/30 bg-emerald-500/[0.04] hover:border-emerald-500/50'
                              : 'border-zinc-800/60 bg-zinc-900/30 hover:border-zinc-700'}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-zinc-600">{phase}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant={variant}>{status}</Badge>
                    {highlight && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    )}
                  </div>
                </div>
                <p className="font-semibold mb-2">{rail}</p>
                <p className="text-zinc-500 text-sm mb-4 leading-relaxed">{desc}</p>
                <p className="text-xs font-mono text-zinc-700 border-t border-zinc-800/60 pt-3">
                  {technical}
                </p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-32 px-6 border-t border-zinc-800/40">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              Be visible before the window closes.
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto text-sm leading-relaxed mb-8">
              The infrastructure buildout phase of agentic commerce is happening now.
              Every week of Intent Graph depth that you don't accumulate cannot be recovered.
            </p>
            <div className="inline-flex items-center gap-3 p-1 rounded-full
                            bg-zinc-900 border border-zinc-800">
              {['Monthly', 'Annual (save 20%)'].map((label, i) => (
                <button key={label} type="button"
                  onClick={() => setIsAnnual(i === 1)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer
                               ${(i === 1) === isAnnual
                                 ? 'bg-violet-600 text-white'
                                 : 'text-zinc-400 hover:text-zinc-200'}`}>
                  {label}
                </button>
              ))}
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {PRICING.map((tier, i) => (
              <AnimatedSection key={tier.name} delay={i * 0.08}
                className={`relative p-8 rounded-2xl border flex flex-col
                            ${tier.highlighted
                              ? 'border-violet-500/50 bg-zinc-900/60 shadow-[0_0_40px_rgba(139,92,246,0.12)]'
                              : 'border-zinc-800/60 bg-zinc-900/20'}`}>
                {tier.highlighted && (
                  <div className="absolute top-0 right-8 -translate-y-1/2">
                    <div className="bg-violet-600 text-white text-xs font-semibold
                                    px-3 py-1 rounded-full shadow-lg">
                      Most popular
                    </div>
                  </div>
                )}
                <p className="text-xs text-zinc-600 mb-1 font-mono">{tier.tagline}</p>
                <h3 className="text-xl font-bold mb-4">{tier.name}</h3>
                <div className="text-5xl font-bold font-mono mb-1">
                  ${isAnnual ? tier.price.annual : tier.price.monthly}
                  {(isAnnual ? tier.price.annual : tier.price.monthly) > 0 && (
                    <span className="text-lg text-zinc-500 font-normal">/mo</span>
                  )}
                </div>
                {isAnnual && tier.price.annual > 0 && (
                  <p className="text-xs text-zinc-600 mb-6">billed annually</p>
                )}
                <ul className="space-y-3.5 mb-8 flex-1 mt-6">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                      <CheckCircle2 className="w-4 h-4 text-violet-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={tier.highlighted ? 'primary' : 'outline'}
                  className="w-full"
                  onClick={() => onNavigate('onboarding')}>
                  {tier.cta}
                </Button>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA — closing declaration ──────────────────────────────── */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[400px] bg-violet-600/10 blur-[130px] rounded-full" />
        </div>
        <AnimatedSection className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-[1.1] tracking-tight">
            The agents are already<br />
            <span className="text-transparent bg-clip-text
                             bg-gradient-to-r from-violet-400 to-violet-600">
              looking for your products.
            </span>
          </h2>
          <p className="text-zinc-400 text-lg mb-10 leading-relaxed">
            The question is whether they can find them.
          </p>
          <Button size="lg" className="h-14 px-10 text-base
                           shadow-[0_0_40px_rgba(139,92,246,0.4)]
                           hover:shadow-[0_0_60px_rgba(139,92,246,0.6)]
                           transition-shadow duration-300"
            onClick={() => onNavigate('onboarding')}>
            Make your store agent-readable <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          {/* v3.2: elintra.algo added */}
          <p className="text-zinc-700 text-xs mt-6">
            Constitutional governance on every transaction ·
            elintra.eth · elintra.algo · elintra.foundation
          </p>
        </AnimatedSection>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-zinc-800/40 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row
                        justify-between items-center gap-6">
          <div className="flex items-center gap-2 font-bold tracking-tight">
            <Zap className="w-4 h-4 text-violet-500" />
            Elintra
            <span className="text-zinc-700 font-normal text-xs ml-1">
              v3.2 · UAG v3.0.0 · Constitutional Commerce
            </span>
          </div>
          <div className="flex gap-6 text-xs text-zinc-600">
            {['elintra.foundation', 'elintra.dev', 'elintra.algo', 'Privacy', 'Terms'].map((link) => (
              <a key={link} href="#" className="hover:text-zinc-400 transition-colors">{link}</a>
            ))}
          </div>
          <p className="text-zinc-700 text-xs">© 2026 Elintra</p>
        </div>
      </footer>
    </div>
  );
};
